import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { viewEmailThreadDetailsV2 } from 'services/mailbox/mailboxServiceV2';
import { useNavbarContext } from 'context/NavbarContext';
import { mailboxV3Keys } from './mailboxV3QueryKeys';

const DEFAULT_LIMIT = 50;

/**
 * Hook for fetching all emails in a thread using React Query's useQuery.
 * Replaces useEmailThreadDetailsV2 for V3 — eliminates the manual fetch +
 * useEffect pattern and gives proper caching / loading states.
 *
 * The query is disabled when pk/sk are falsy, so no guard useEffect is needed.
 * onSuccess handles the mark-read side effect and notification count refresh.
 *
 * @param {{ pk: string|null, sk: string|null }} emailThreadPkSk
 * @param {Function} setReadEmails - Setter for local optimistic read-state tracking
 */
const useEmailThreadDetailsV3 = (emailThreadPkSk, setReadEmails = () => {}) => {
  const { pk, sk } = emailThreadPkSk || {};
  const queryClient = useQueryClient();
  const { notificationData } = useNavbarContext();

  const { data, isLoading, isFetching, error } = useQuery(
    mailboxV3Keys.threadDetail(pk, sk),
    () => viewEmailThreadDetailsV2(pk, sk, DEFAULT_LIMIT, 0),
    {
      enabled: Boolean(pk && sk),
      // Avoid merged arrays when switching threads (can look like a growing "thread" in Sent).
      structuralSharing: false,
      // Always fetch fresh thread detail when switching between threads
      staleTime: 0,
      onSuccess: () => {
        // Optimistically mark the thread as read in local state
        if (pk) {
          setReadEmails((prev) => ({ ...prev, [pk]: true }));
        }
        notificationData?.loadNotificationCount();
      },
      onError: (err) => {
        console.error('[useEmailThreadDetailsV3] fetch error:', err);
      },
      select: (res) => {
        const details = res?.data?.email_thread_details ?? [];
        // Reverse so oldest message is at top, newest reply at bottom (chronological).
        // Clone rows so React Query structural sharing never reuses prior thread objects.
        return details
          .slice()
          .reverse()
          .map((row) => ({ ...row }));
      },
    }
  );

  const emailDetails = data ?? [];

  /**
   * Allows local optimistic append of a sent reply without waiting for a refetch.
   * Called by ReplyComposer's onSent callback.
   */
  const appendReply = useCallback(
    (replyData) => {
      if (replyData == null) return;
      queryClient.setQueryData(mailboxV3Keys.threadDetail(pk, sk), (old) => {
        if (!old) return old;
        const currentDetails = old?.data?.email_thread_details ?? [];
        return {
          ...old,
          data: {
            ...old.data,
            email_thread_details: [...currentDetails, replyData],
          },
        };
      });
    },
    [queryClient, pk, sk]
  );

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: mailboxV3Keys.threadDetail(pk, sk) });
  }, [queryClient, pk, sk]);

  return {
    emailDetails,
    appendReply,
    invalidate,
    loading: isLoading,
    isFetching,
    error,
  };
};

export default useEmailThreadDetailsV3;
